import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { AuthProvider } from './lib/auth.jsx'
import { ToastProvider } from './lib/toast.jsx'
import { maybeBootstrapDevSession } from './lib/devAuth.js'
import { initSentry, SentryErrorBoundary } from './lib/sentry.js'
import App from './App.jsx'
import './index.css'

initSentry()
maybeBootstrapDevSession()

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime:    1_000 * 60 * 60 * 24,    // keep cached 24h for persist
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'arise-query-cache-v1',
  throttleTime: 1000,
})

function CrashFallback() {
  return (
    <div className="min-h-screen grid place-items-center p-6 text-center">
      <div className="space-y-3 max-w-md">
        <div className="font-display text-2xl text-accent">◆ Crash ◆</div>
        <p className="opacity-80">Etwas ist schief gelaufen. Bug wurde geloggt.</p>
        <button onClick={() => location.reload()} className="px-4 py-2 rounded-themed bg-accent font-semibold">
          Reload
        </button>
      </div>
    </div>
  )
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <PersistQueryClientProvider
        client={qc}
        persistOptions={{
          persister,
          maxAge: 1000 * 60 * 60 * 24 * 7,   // 7 days
          // Skip persisting heavy or sensitive queries
          dehydrateOptions: {
            shouldDehydrateQuery: (q) => {
              const k = q.queryKey?.[0]
              if (k === 'coach-history') return false
              if (k === 'db-view')       return false
              return q.state.status === 'success'
            },
          },
        }}
      >
        <SentryErrorBoundary fallback={<CrashFallback />}>
          <AuthProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </AuthProvider>
        </SentryErrorBoundary>
      </PersistQueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
)
